<template lang="html">
	<div class="appointment-modal">
		<header class="appointment-modal__header">
			<div class="appointment-modal__title-group">
				<h2 class="appointment-modal__title">{{ data && data.rdv ? 'Modifier le rendez-vous' : 'Planifier un rendez-vous' }}</h2>
				<p class="appointment-modal__subtitle">{{ data && data.rdv ? 'Modifiez les informations du rendez-vous' : 'Planifiez un rendez-vous avec votre client' }}</p>
			</div>
			<div class="appointment-modal__type-field">
				<label class="appointment-modal__label appointment-modal__label--required">
					Type de rendez-vous
				</label>
				<custom-select
					v-validate="'required|select'"
					v-model="typeOfAppointment"
					:items="typesOfAppointment"
					name="type"
					:solo="true"
					:class="{ 'appointment-modal__select--error': errors.has('type') }"
				/>
				<span v-if="errors.has('type')" class="appointment-modal__error">
					Veuillez sélectionner un type de rendez-vous
				</span>
			</div>
		</header>

		<form class="appointment-modal__form" @submit.prevent="send">
			<div class="appointment-modal__section">
				<h3 class="appointment-modal__section-title">
					<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
						<path d="M12.667 2.667H3.333C2.597 2.667 2 3.264 2 4v9.333c0 .737.597 1.334 1.333 1.334h9.334c.736 0 1.333-.597 1.333-1.334V4c0-.736-.597-1.333-1.333-1.333z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
						<path d="M10.667 1.333v2.667M5.333 1.333v2.667M2 6.667h12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
					</svg>
					Début du rendez-vous
				</h3>
				<div class="appointment-modal__row">
					<div class="appointment-modal__field">
						<label class="appointment-modal__label appointment-modal__label--required">Date de début</label>
						<div class="appointment-modal__input-wrapper" :class="{ 'appointment-modal__input-wrapper--error': errors.has('Date de début') }">
							<input
								v-inputUI
								v-validate="'required|min_date:' + computedToday"
								v-model="beginDate"
								type="date"
								name="Date de début"
								class="appointment-modal__input"
								data-vv-validate-on="blur"
							>
						</div>
						<span v-if="errors.has('Date de début')" class="appointment-modal__error">
							{{ errors.first('Date de début') }}
						</span>
					</div>
					<div class="appointment-modal__field">
						<label class="appointment-modal__label appointment-modal__label--required">Heure de début</label>
						<div class="appointment-modal__input-wrapper" :class="{ 'appointment-modal__input-wrapper--error': errors.has('Heure de début') }">
							<custom-select
								v-validate="'required|select'"
								v-model="beginHour"
								name="Heure de début"
								:items="hoursPossible"
							/>
						</div>
						<span v-if="errors.has('Heure de début')" class="appointment-modal__error">
							Veuillez sélectionner une heure
						</span>
					</div>
				</div>
			</div>

			<div class="appointment-modal__section">
				<h3 class="appointment-modal__section-title">
					<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
						<path d="M12.667 2.667H3.333C2.597 2.667 2 3.264 2 4v9.333c0 .737.597 1.334 1.333 1.334h9.334c.736 0 1.333-.597 1.333-1.334V4c0-.736-.597-1.333-1.333-1.333z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
						<path d="M10.667 1.333v2.667M5.333 1.333v2.667M2 6.667h12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
					</svg>
					Fin du rendez-vous
				</h3>
				<div class="appointment-modal__row">
					<div class="appointment-modal__field">
						<label class="appointment-modal__label appointment-modal__label--required">Date de fin</label>
						<div class="appointment-modal__input-wrapper" :class="{ 'appointment-modal__input-wrapper--error': errors.has('Date de fin') }">
							<input
								v-inputUI
								v-validate="'required'"
								v-model="endDate"
								type="date"
								name="Date de fin"
								class="appointment-modal__input"
								data-vv-validate-on="blur"
							>
						</div>
						<span v-if="errors.has('Date de fin')" class="appointment-modal__error">
							{{ errors.first('Date de fin') }}
						</span>
					</div>
					<div class="appointment-modal__field">
						<label class="appointment-modal__label appointment-modal__label--required">Heure de fin</label>
						<div class="appointment-modal__input-wrapper" :class="{ 'appointment-modal__input-wrapper--error': errors.has('Heure de fin') }">
							<custom-select
								v-validate="'required|select|min_value:' + (parseInt(beginHour, 10) + 1)"
								v-model="endHour"
								name="Heure de fin"
								:items="hoursPossible"
							/>
						</div>
						<span v-if="errors.has('Heure de fin')" class="appointment-modal__error">
							Veuillez sélectionner une heure valide
						</span>
					</div>
				</div>
			</div>

			<div class="appointment-modal__section">
				<h3 class="appointment-modal__section-title">
					<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
						<path d="M14 10l-4-4-4 4-4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
					</svg>
					Informations complémentaires
				</h3>
				<div class="appointment-modal__field appointment-modal__field--full">
					<label class="appointment-modal__label">Note (optionnel)</label>
					<textarea
						v-model="note"
						class="appointment-modal__textarea"
						placeholder="Ajoutez une note pour ce rendez-vous..."
						rows="3"
					></textarea>
				</div>
			</div>

			<div v-if="needSelectClient" class="appointment-modal__section">
				<h3 class="appointment-modal__section-title">
					<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
						<path d="M13.333 14v-1.333A2.667 2.667 0 0010.667 10H5.333a2.667 2.667 0 00-2.666 2.667V14M8 7.333A2.667 2.667 0 108 2a2.667 2.667 0 000 5.333z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
					</svg>
					Client
				</h3>
				<div class="appointment-modal__field appointment-modal__field--full">
					<label class="appointment-modal__label appointment-modal__label--required">Sélectionner un client</label>
					<custom-select-user v-model="myuser" :items="clients" />
				</div>
			</div>

			<footer class="appointment-modal__footer">
				<button type="button" class="appointment-modal__btn appointment-modal__btn--secondary" @click="close">
					Annuler
				</button>
				<button type="submit" class="appointment-modal__btn appointment-modal__btn--primary">
					Enregistrer
				</button>
			</footer>
		</form>
	</div>
</template>

<script>
import { mapGetters } from 'vuex'
import CustomSelect from '@/common/components/Form/CustomSelect'
import CustomSelectUser from '@/common/components/Form/CustomSelectUser'
import { resolveDate } from '@/common/services/ResolveDate'
export default {
	name: 'ModalTakeAppointment',
	props: ['data'],
	components: {
		CustomSelect,
		CustomSelectUser
	},
	data () {
		return {
			typesOfAppointment: ['Visio Call', 'Appel Téléphonique', 'Rendez-vous physique'],
			typeOfAppointment: null,
			beginDate: null,
			beginHour: null,
			endDate: null,
			endHour: null,
			note: null,
			myuser: null,
			hoursPossible: [...Array(24).keys()].map(item => ('00' + item).slice(-2)),
			needSelectClient: false
		}
	},
	mounted () {
		const rdv = this.data && this.data.rdv ? this.data.rdv : null
		const client = this.data && !this.data.rdv ? this.data : null

		if (rdv) {
			// Mode édition : pré-remplissage
			const typeMap = {
				'Rendez-vous': 'Rendez-vous physique',
				'Appel téléphonique': 'Appel Téléphonique',
				'Appel Téléphonique': 'Appel Téléphonique',
				'Visio Call': 'Visio Call'
			}
			this.typeOfAppointment = typeMap[rdv.type] || rdv.type
			this.beginDate = this.$moment(rdv.hour.start).format('YYYY-MM-DD')
			this.beginHour = this.$moment(rdv.hour.start).format('HH')
			this.endDate = this.$moment(rdv.hour.end).format('YYYY-MM-DD')
			this.endHour = this.$moment(rdv.hour.end).format('HH')
			this.note = rdv.description || null
			this.myuser = { id: rdv.clientId }
		} else if (client) {
			this.myuser = client
		} else {
			this.needSelectClient = true
		}
	},
	methods: {
		close () {
			this.$emit('close')
		},
		send () {
			this.$validator.validate().then((validation) => {
				if (validation) {
					const payload = {
						user_id: this.myuser.id,
						advisor_id: this.user.id,
						start_date: resolveDate(this.beginDate),
						end_date: resolveDate(this.endDate),
						start_time: this.beginHour + ':00',
						end_time: this.endHour + ':00',
						description: this.note,
						type: (this.typeOfAppointment === 'Rendez-vous physique') ? 'Rendez-vous' : this.typeOfAppointment
					}

					const rdv = this.data && this.data.rdv ? this.data.rdv : null
					const action = rdv
						? this.$store.dispatch('updateAppointment', { id: rdv.id, data: payload })
						: this.$store.dispatch('createAppointment', payload)

					action.then(() => {
						this.$notify({
							type: 'success',
							group: 'default',
							title: rdv ? 'Rendez-vous modifié avec succès.' : 'Votre prise de rendez-vous a bien été enregistrée.',
							text: ''
						})
						this.close()
					}).catch(() => {
						this.$notify({
							type: 'error',
							group: 'default',
							title: 'Une erreur est survenue',
							text: 'Veuillez vérifier les informations que vous avez renseignées'
						})
					})
				}
			})
		}
	},
	computed: {
		...mapGetters({
			user: 'user',
			clients: 'getClients'
		}),
		computedToday () {
			const d = new Date()
			return d.getFullYear() + '-' + ('00' + (d.getMonth() + 1)).slice(-2) + '-' + ('00' + (d.getDate() - 1)).slice(-2)
		}
	}
}
</script>

<style lang="scss" scoped>
$primary-color: #AF9376;
$primary-dark: #8B7355;
$error-color: #dc3545;
$border-color: #E5E7EB;
$text-color: #1A1A1A;
$text-muted: #6B7280;
$bg-section: #F9FAFB;

.appointment-modal {
	padding: 1.5rem;
	max-width: 600px;
	font-family: "Lato", sans-serif;
}

// Header
.appointment-modal__header {
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	gap: 1.5rem;
	padding-bottom: 1.5rem;
	border-bottom: 1px solid $border-color;
	margin-bottom: 1.5rem;

	@media (max-width: 576px) {
		flex-direction: column;
	}
}

.appointment-modal__title-group {
	flex: 1;
}

.appointment-modal__title {
	font: 700 1.5rem "Lato", sans-serif;
	color: $text-color;
	margin: 0 0 0.25rem;
}

.appointment-modal__subtitle {
	font: 400 0.875rem "Lato", sans-serif;
	color: $text-muted;
	margin: 0;
}

.appointment-modal__type-field {
	min-width: 200px;

	@media (max-width: 576px) {
		width: 100%;
	}
}

// Form
.appointment-modal__form {
	display: flex;
	flex-direction: column;
	gap: 1.5rem;
}

// Sections
.appointment-modal__section {
	background: $bg-section;
	border-radius: 10px;
	padding: 1rem 1.25rem;
}

.appointment-modal__section-title {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	font: 600 0.875rem "Lato", sans-serif;
	color: $primary-color;
	margin: 0 0 1rem;
	text-transform: uppercase;
	letter-spacing: 0.025em;

	svg {
		flex-shrink: 0;
	}
}

// Row (for 2 columns)
.appointment-modal__row {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 1rem;

	@media (max-width: 576px) {
		grid-template-columns: 1fr;
	}
}

// Field
.appointment-modal__field {
	display: flex;
	flex-direction: column;
	gap: 0.5rem;

	&--full {
		width: 100%;
	}
}

// Label
.appointment-modal__label {
	font: 500 0.8125rem "Lato", sans-serif;
	color: $text-muted;

	&--required::after {
		content: " *";
		color: $error-color;
		font-weight: 600;
	}
}

// Input wrapper
.appointment-modal__input-wrapper {
	position: relative;

	&--error {
		.appointment-modal__input,
		:deep(.custom-select) {
			border-color: $error-color !important;
		}
	}
}

// Input
.appointment-modal__input {
	width: 100%;
	padding: 0.75rem 1rem;
	border: 1px solid $border-color;
	border-radius: 8px;
	font: 400 0.875rem "Lato", sans-serif;
	color: $text-color;
	background: #fff;
	transition: border-color 0.2s ease, box-shadow 0.2s ease;

	&:focus {
		outline: none;
		border-color: $primary-color;
		box-shadow: 0 0 0 3px rgba($primary-color, 0.1);
	}

	&::placeholder {
		color: #9CA3AF;
	}
}

// Textarea
.appointment-modal__textarea {
	width: 100%;
	padding: 0.75rem 1rem;
	border: 1px solid $border-color;
	border-radius: 8px;
	font: 400 0.875rem "Lato", sans-serif;
	color: $text-color;
	background: #fff;
	resize: vertical;
	min-height: 80px;
	transition: border-color 0.2s ease, box-shadow 0.2s ease;

	&:focus {
		outline: none;
		border-color: $primary-color;
		box-shadow: 0 0 0 3px rgba($primary-color, 0.1);
	}

	&::placeholder {
		color: #9CA3AF;
	}
}

// Error message
.appointment-modal__error {
	display: flex;
	align-items: center;
	gap: 0.25rem;
	font: 400 0.75rem "Lato", sans-serif;
	color: $error-color;
	margin-top: 0.25rem;

	&::before {
		content: "!";
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 14px;
		height: 14px;
		background: $error-color;
		color: #fff;
		border-radius: 50%;
		font-size: 0.625rem;
		font-weight: 700;
	}
}

// Select error state
.appointment-modal__select--error {
	:deep(.custom-select) {
		border-color: $error-color !important;
	}
}

// Footer
.appointment-modal__footer {
	display: flex;
	justify-content: flex-end;
	gap: 0.75rem;
	padding-top: 1rem;
	border-top: 1px solid $border-color;
	margin-top: 0.5rem;
}

// Buttons
.appointment-modal__btn {
	padding: 0.75rem 1.5rem;
	border-radius: 8px;
	font: 600 0.875rem "Lato", sans-serif;
	cursor: pointer;
	transition: all 0.2s ease;
	border: none;

	&--primary {
		background: $primary-color;
		color: #fff;

		&:hover {
			background: $primary-dark;
			transform: translateY(-1px);
		}

		&:active {
			transform: translateY(0);
		}
	}

	&--secondary {
		background: transparent;
		color: $text-muted;
		border: 1px solid $border-color;

		&:hover {
			background: #F3F4F6;
			color: $text-color;
		}
	}
}

// Deep styles for custom-select component
:deep(.custom-select) {
	border: 1px solid $border-color;
	border-radius: 8px;
	background: #fff;
	padding: 0.5rem 1rem;
	min-height: 44px;
	transition: border-color 0.2s ease, box-shadow 0.2s ease;

	&:hover {
		border-color: darken($border-color, 10%);
	}

	&:focus-within {
		border-color: $primary-color;
		box-shadow: 0 0 0 3px rgba($primary-color, 0.1);
	}

	// Placeholder text
	.custom-select__placeholder,
	.placeholder {
		color: #9CA3AF;
		font: 400 0.875rem "Lato", sans-serif;
	}

	// Selected value
	.custom-select__value,
	.selected-value {
		color: $text-color;
		font: 400 0.875rem "Lato", sans-serif;
	}

	// Dropdown arrow
	.custom-select__arrow,
	.arrow {
		color: $text-muted;
		transition: transform 0.2s ease;
	}

	// Dropdown menu
	.custom-select__dropdown,
	.dropdown {
		border: 1px solid $border-color;
		border-radius: 8px;
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
		margin-top: 4px;
		background: #fff;
		overflow: hidden;
	}

	// Dropdown items
	.custom-select__item,
	.dropdown-item {
		padding: 0.625rem 1rem;
		font: 400 0.875rem "Lato", sans-serif;
		color: $text-color;
		cursor: pointer;
		transition: background-color 0.15s ease;

		&:hover {
			background: rgba($primary-color, 0.08);
		}

		&.selected,
		&.active {
			background: rgba($primary-color, 0.12);
			color: $primary-color;
			font-weight: 500;
		}
	}
}

// Custom select user component
:deep(.custom-select-user) {
	border: 1px solid $border-color;
	border-radius: 8px;
	background: #fff;
	padding: 0.5rem 1rem;
	min-height: 44px;
	transition: border-color 0.2s ease, box-shadow 0.2s ease;

	&:hover {
		border-color: darken($border-color, 10%);
	}

	&:focus-within {
		border-color: $primary-color;
		box-shadow: 0 0 0 3px rgba($primary-color, 0.1);
	}
}
</style>



// WEBPACK FOOTER //
// TakeAppointment.vue?5139034a